/**
 * App Sidebar Component
 * Main navigation sidebar with role-based menu items
 *
 * Responsive behavior:
 * - Mobile (< md): Hidden, hamburger menu shows overlay
 * - Tablet (md-lg): Always visible, collapsed by default (icons only)
 * - Desktop (>= lg): Always visible, expanded by default, collapsible
 */

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import {
  LayoutDashboard,
  MapPin,
  Users,
  ChefHat,
  User,
  LogOut,
  Settings,
  PanelRight,
  CalendarDays,
  MessageSquare,
  FileBarChart,
  BookOpenText,
  ShoppingBasket,
  ClipboardList,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { fetchPendingMembers } from '@/lib/api/members';

interface NavItem {
  label: string;
  icon: any;
  href: string;
  matchPrefix?: string;
  roles?: string[]; // If specified, only show for these roles
}

interface AppSidebarProps {
  isCollapsed?: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  onCollapseToggle?: () => void;
}

const navItems: NavItem[] = [
  {
    label: 'RSVP',
    icon: LayoutDashboard,
    href: '/rsvp',
  },
  {
    label: 'Admin Dashboard',
    icon: Settings,
    href: '/admin/dashboard',
    roles: ['ADMIN'],
  },
  {
    label: 'Pickup Locations',
    icon: MapPin,
    href: '/admin/pickup-locations',
    roles: ['ADMIN'],
  },
  {
    label: 'Menus',
    icon: CalendarDays,
    href: '/admin/menus',
    roles: ['ADMIN'],
  },
  {
    label: 'Kitchen Library',
    icon: BookOpenText,
    href: '/admin/library/recipes',
    matchPrefix: '/admin/library',
    roles: ['ADMIN', 'CATERER'],
  },
  {
    label: 'Helper Schedule',
    icon: ClipboardList,
    href: '/kitchen/helper-schedule',
    roles: ['ADMIN', 'CATERER'],
  },
  {
    label: 'Procurement',
    icon: ShoppingBasket,
    href: '/admin/procurement',
    roles: ['ADMIN'],
  },
  {
    label: 'Members',
    icon: Users,
    href: '/admin/members',
    roles: ['ADMIN'],
  },
  {
    label: 'Feedback',
    icon: MessageSquare,
    href: '/admin/feedback',
    roles: ['ADMIN'],
  },
  {
    label: 'RSVP Reports',
    icon: FileBarChart,
    href: '/admin/reports',
    roles: ['ADMIN'],
  },
  {
    label: 'System Settings',
    icon: Settings,
    href: '/admin/system-settings',
    roles: ['ADMIN'],
  },
  {
    label: 'Caterer Dashboard',
    icon: ChefHat,
    href: '/caterer/dashboard',
    roles: ['CATERER'],
  },
  {
    label: 'Procurement',
    icon: ShoppingBasket,
    href: '/caterer/procurement',
    roles: ['CATERER'],
  },
  {
    label: 'Feedback',
    icon: MessageSquare,
    href: '/caterer/feedback',
    roles: ['CATERER'],
  },
];

export function AppSidebar({
  isCollapsed: collapsedProp,
  setIsCollapsed: setCollapsedProp,
  isMobileMenuOpen: mobileMenuOpenProp,
  setIsMobileMenuOpen: setMobileMenuOpenProp,
  onCollapseToggle,
}: AppSidebarProps = {}) {
  const { user, logout } = useAuthStore();
  const { showToast } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Use prop if provided, otherwise use internal state
  const isCollapsed = collapsedProp ?? internalCollapsed;
  const setIsCollapsed = setCollapsedProp ?? setInternalCollapsed;
  const isOpen = mobileMenuOpenProp ?? internalMobileMenuOpen;
  const setIsOpen = setMobileMenuOpenProp ?? setInternalMobileMenuOpen;

  const isAdmin = user?.role === 'ADMIN';

  // Fetch pending members count on component mount
  useEffect(() => {
    if (!isAdmin) return;

    const fetchPendingCount = async () => {
      try {
        const response = await fetchPendingMembers();
        setPendingCount(response.count);
      } catch (error) {
        console.error('Failed to fetch pending members count:', error);
      }
    };

    fetchPendingCount();
  }, [isAdmin]);

  const handleLogout = async () => {
    try {
      await logout();
      showToast('Logged out successfully', 'success');
      navigate('/login', { replace: true });
    } catch (error) {
      showToast('Failed to logout', 'error');
    }
  };

  if (!user) return null;

  // Filter nav items based on user role
  const visibleNavItems = navItems.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(user.role);
  });

  const isActive = (item: NavItem) =>
    item.matchPrefix
      ? location.pathname.startsWith(item.matchPrefix)
      : location.pathname === item.href;

  const profileLink = (
    <Link
      to="/profile"
      aria-label={`Profile: ${user.name}`}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg bg-background/60 hover:bg-accent transition-colors',
        // On mobile, always left-aligned. On desktop, center when collapsed
        isCollapsed && 'md:justify-center'
      )}
    >
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <User className="h-4 w-4 text-primary" />
      </div>
      {/* Always show on mobile, hide on desktop when collapsed */}
      <div className={cn('flex-1 min-w-0', isCollapsed && 'md:hidden')}>
        <p className="text-sm font-medium truncate text-foreground">{user.name}</p>
        <p className="text-xs text-muted-foreground capitalize">
          {user.role.toLowerCase()}
        </p>
      </div>
    </Link>
  );

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full bg-background/60 backdrop-blur-md border-r border-border/50 flex flex-col z-40 transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64',
          // Mobile: hidden by default, show as overlay when open
          // Tablet/Desktop: always visible
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {/* Header */}
        <div className="p-6 border-b border-border transition-all duration-300 h-[68px] flex items-center justify-center">
          <Link to="/" className={cn('flex items-center gap-2', !isCollapsed && 'w-full')}>
            {/* Always show on mobile, hide on desktop when collapsed */}
            <div className={cn('transition-all duration-300 whitespace-nowrap', isCollapsed && 'md:hidden')}>
              <h1 className="text-lg font-semibold text-foreground">FMB RSVP</h1>
            </div>
          </Link>

          {/* Collapse button - only visible when collapsed on desktop */}
          {onCollapseToggle && isCollapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onCollapseToggle}
                  className="hidden md:flex p-2 rounded-lg hover:bg-accent transition-colors"
                  aria-label="Expand sidebar"
                >
                  <PanelRight className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="hidden md:block">
                Expand sidebar
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const isMembersLink = item.href === '/admin/members';

            const navLink = (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  // On mobile, always show labels. On tablet/desktop, respect collapse state
                  isCollapsed ? 'md:justify-center' : '',
                  active
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {/* Always show label on mobile, respect collapse state on desktop */}
                <span className={cn('whitespace-nowrap', isCollapsed && 'md:hidden')}>{item.label}</span>
                {/* Badge for pending members count */}
                {isMembersLink && pendingCount > 0 && !isCollapsed && (
                  <Badge variant="destructive" className="ml-auto text-xs">
                    {pendingCount}
                  </Badge>
                )}
              </Link>
            );

            if (!isCollapsed) {
              return navLink;
            }

            return (
              <Tooltip key={item.href}>
                <TooltipTrigger asChild>{navLink}</TooltipTrigger>
                <TooltipContent side="right" className="hidden md:block">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border space-y-2 transition-all duration-300 md:h-[68px] flex flex-col justify-center">
          {/* User info - clickable to go to profile */}
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>{profileLink}</TooltipTrigger>
              <TooltipContent side="right" className="hidden md:block">
                Profile
              </TooltipContent>
            </Tooltip>
          ) : (
            profileLink
          )}

          {/* Logout - mobile only */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-3 text-red-600 hover:text-red-700 hover:bg-red-50 md:hidden"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}
